import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertTriangle, XCircle, RefreshCw, Copy, Check } from 'lucide-react';
import { apiDeliverability } from '../../api';
import styles from './Mailer.module.css';

/**
 * Live SPF / DKIM / DMARC audit of the sending domain.
 *
 * This panel exists because authentication failures are invisible from the
 * sending side: mail with no DMARC record sends fine, reports success, and gets
 * filed in Junk. Reading the DNS is the only way to know, so the check runs on
 * open and shows the exact record to paste when something is missing.
 */

const ICONS = {
  pass: <CheckCircle size={18} color="var(--success)" />,
  warn: <AlertTriangle size={18} color="var(--warning)" />,
  fail: <XCircle size={18} color="var(--danger)" />,
};

const CheckDetail = ({ name, check }) => {
  const [copied, setCopied] = useState(false);

  // The record value inside the fix text, so it can be copied in one click
  // rather than selected out of a sentence.
  const quoted = /"([^"]+)"/.exec(check.fix || '')?.[1];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(quoted);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the value is visible either way.
    }
  };

  return (
    <div className={styles.checkRow}>
      <div className={styles.checkIcon}>{ICONS[check.status] || ICONS.warn}</div>
      <div className={styles.checkBody}>
        <div className={styles.checkName}>{name}</div>
        <div className={styles.checkMessage}>{check.message}</div>

        {check.record && (
          <code className={`${styles.mono} ${styles.recordBlock}`}>{check.record}</code>
        )}
        {Array.isArray(check.records) && typeof check.records[0] === 'string' && (
          <code className={`${styles.mono} ${styles.recordBlock}`}>{check.records.join('\n')}</code>
        )}

        {check.fix && (
          <div className={styles.checkFix}>
            <strong>Fix:</strong> {check.fix}
            {quoted && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={copy}
                style={{ marginTop: 8, display: 'flex', gap: 6 }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy record value'}
              </button>
            )}
          </div>
        )}

        {check.caveat && (
          <div className={styles.checkFix} style={{ borderLeftColor: 'var(--warning)' }}>
            <strong>Note:</strong> {check.caveat}
          </div>
        )}
      </div>
    </div>
  );
};

const DomainHealth = ({ jwtToken, domain, domains = [] }) => {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(domain);

  useEffect(() => { setSelected(domain); }, [domain]);

  const runCheck = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setAudit(await apiDeliverability(jwtToken, selected));
    } catch (e) {
      setError(e.message || 'Could not run the DNS check');
    } finally {
      setLoading(false);
    }
  }, [jwtToken, selected]);

  useEffect(() => { runCheck(); }, [runCheck]);

  return (
    <div className="card">
      <div className="page-header" style={{ marginBottom: 8 }}>
        <div>
          <h3 className="card-title">Domain authentication</h3>
          <p className="page-subtitle">
            {audit ? `Live DNS for ${audit.domain}` : 'Checking your sending domain’s DNS records'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* One Zoho account can send from several domains, and each needs its
              own SPF, DKIM and DMARC. Checking only the default from-address
              would leave the others silently unauthenticated. */}
          {domains.length > 1 && (
            <select
              className="input"
              style={{ width: 'auto' }}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              {domains.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <button className="btn btn-ghost btn-sm" onClick={runCheck} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} /> {loading ? 'Checking…' : 'Re-check'}
          </button>
        </div>
      </div>

      {error && <div className={styles.dangerBox}>{error}</div>}

      {loading && !audit && (
        <div className="empty"><span className="spinner" style={{ width: 28, height: 28 }} /></div>
      )}

      {audit && (
        <>
          <div
            className={
              audit.verdict === 'fail' ? styles.dangerBox
                : audit.verdict === 'warn' ? styles.warnBox
                  : styles.infoBox
            }
          >
            <div className={styles.boxTitle}>
              {ICONS[audit.verdict]} {audit.summary}
            </div>
            {audit.verdict === 'fail' && (
              <div>
                Fix these before sending volume. Authentication is what stops mail being
                rejected outright — it is the floor, not a guarantee of the inbox.
              </div>
            )}
          </div>

          <div>
            <CheckDetail name="SPF" check={audit.checks.spf} />
            <CheckDetail name="DKIM" check={audit.checks.dkim} />
            <CheckDetail name="DMARC" check={audit.checks.dmarc} />
            <CheckDetail name="MX" check={audit.checks.mx} />
          </div>
        </>
      )}
    </div>
  );
};

export default DomainHealth;
